import React from "react";

import {
  Headset,
  Phone,
  Mail,
  Clock3,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

// =========================================================
// SUPPORT DATA
// =========================================================

const callItems = [
  {
    id: 1,
    title: "Customer Support",
    value: "+919818843759",
    href: "tel:+919818843759",
  },
  {
    id: 2,
    title: "Order Assistance",
    value: "+919650477664",
    href: "tel:+919650477664",
  },
];

const emailItems = [
  {
    id: 1,
    title: "Email Support",
    value: "support@chaafo.com",
    href: "mailto:support@chaafo.com",
  },
];

// =========================================================
// SUPPORT CARD
// =========================================================

function SupportCard({ title, value, href, icon: Icon, type = "default" }) {
  const iconStyle =
    type === "email"
      ? "bg-[#eef4ff] text-[#2563eb]"
      : "bg-[#ecfdf5] text-[#16a34a]";

  return (
    <a
      href={href}
      className="
        group
        flex
        min-w-0
        items-center
        justify-between
        gap-3

        rounded-[14px]
        border
        border-[#e2e6ec]

        bg-white

        px-3.5
        py-3.5

        transition

        hover:border-[#cfd6e0]
        hover:bg-[#fafbfc]

        active:scale-[0.995]

        sm:px-4
        sm:py-4

        lg:px-5
      "
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`
            flex
            h-11
            w-11
            shrink-0
            items-center
            justify-center
            rounded-full

            sm:h-12
            sm:w-12

            ${iconStyle}
          `}
        >
          <Icon size={21} strokeWidth={2} />
        </div>

        <div className="min-w-0">
          <p
            className="
              truncate
              text-[13px]
              font-semibold
              text-[#172033]

              sm:text-[14px]
            "
          >
            {title}
          </p>

          <p
            className="
              mt-0.5
              truncate
              text-[11px]
              text-[#8b97ab]

              sm:text-[12px]
              lg:text-[13px]
            "
          >
            {value}
          </p>
        </div>
      </div>

      <ChevronRight
        size={18}
        strokeWidth={2}
        className="
          shrink-0
          text-[#98a2b3]
          transition
          group-hover:translate-x-0.5
          group-hover:text-[#2c2268]
        "
      />
    </a>
  );
}

// =========================================================
// SECTION HEADING
// =========================================================

function SectionHeading({ children }) {
  return (
    <h3
      className="
        mb-3
        text-[12px]
        font-semibold
        uppercase
        tracking-[0.06em]
        text-[#555f72]

        sm:text-[13px]
      "
    >
      {children}
    </h3>
  );
}

// =========================================================
// HELP SUPPORT
// =========================================================

export default function HelpSupport() {
  return (
    <div className="w-full bg-[#edf5ff]">
      <div
        className="
    border-b
    border-[#edf0f4]
  "
      >
        {/* ================================================= */}
        {/* MAIN WHITE CONTAINER */}
        {/* ================================================= */}

        <div
          className="
            overflow-hidden

            rounded-[18px]

            border
            border-[#edf0f4]

            bg-white

            shadow-[0_3px_12px_rgba(15,23,42,0.04)]

            sm:rounded-[22px]
          "
        >
          {/* =============================================== */}
          {/* PAGE HEADER */}
          {/* =============================================== */}

          <div
            className="
              border-b
              border-[#edf0f4]

              px-4
              py-4

              sm:px-6
              sm:py-5

              lg:px-7
              lg:py-6
            "
          >
            <h1
              className="
                text-[21px]
                font-bold
                tracking-[-0.02em]
                text-[#172033]

                sm:text-[24px]
                lg:text-[28px]
              "
            >
              Help &amp; Support
            </h1>

            <p
              className="
                mt-1
                text-[11px]
                text-[#98a2b3]

                sm:text-[12px]
                lg:text-[13px]
              "
            >
              Need assistance? Our support team is here to help.
            </p>
          </div>

          {/* =============================================== */}
          {/* CONTENT */}
          {/* =============================================== */}

          <div
            className="
  grid
  grid-cols-1

  lg:grid-cols-[280px_minmax(0,1fr)]
  xl:grid-cols-[320px_minmax(0,1fr)]

  [@media(pointer:coarse)]:grid-cols-1
"
          >
            {/* ============================================= */}
            {/* LEFT INFO */}
            {/* ============================================= */}

            <aside
              className="
    border-b
    border-[#edf0f4]
    bg-[#fbfcfe]
    px-4
    py-4
    sm:px-6
    lg:border-b-0
    lg:border-r
    lg:px-5
    lg:py-5
    [@media(pointer:coarse)]:border-b
[@media(pointer:coarse)]:border-r-0
[@media(pointer:coarse)]:px-4
[@media(pointer:coarse)]:py-4
  "
            >
              <div
                className="
                  flex
                  flex-col
                  items-center
                  text-center

                  lg:items-start
                  lg:text-left
                "
              >
                <div
                  className="
    flex
    h-[60px]
    w-[60px]
    items-center
    justify-center
    rounded-full
    bg-[#eeeafd]
    text-[#6654c5]
    sm:h-[64px]
    sm:w-[64px]
  "
                >
                  <Headset size={28} strokeWidth={1.8} />
                </div>

                <h2
                  className="
                    mt-3
                    text-[18px]
                    font-bold
                    text-[#172033]

                    sm:text-[20px]
                  "
                >
                  We&apos;re here to help
                </h2>

                <p
                  className="
                    mt-2
                    max-w-[420px]

                    text-[11px]
                    leading-5
                    text-[#8b97ab]

                    sm:text-[12px]

                    lg:max-w-[250px]
                  "
                >
                  Contact our support team for help with orders, payments,
                  account queries or general assistance.
                </p>

                {/* QUICK INFO */}

                <div
                  className="
                    mt-5
                    flex
                    w-full
                    max-w-[420px]
                    items-start
                    gap-3

                    rounded-[12px]

                    border
                    border-[#e8e4f5]

                    bg-[#f6f4fc]

                    p-3.5

                    lg:max-w-none
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center

                      rounded-full

                      bg-white
                      text-[#5b4bab]
                    "
                  >
                    <MessageCircle size={17} />
                  </div>

                  <div className="min-w-0 text-left">
                    <p className="text-[11px] font-semibold text-[#343044]">
                      Quick assistance
                    </p>

                    <p className="mt-0.5 text-[10px] leading-4 text-[#8a8595]">
                      Select a support option and connect directly with our
                      team.
                    </p>
                  </div>
                </div>
              </div>
            </aside>

            {/* ============================================= */}
            {/* RIGHT SUPPORT OPTIONS */}
            {/* ============================================= */}

            <main
              className="
                min-w-0

                px-4
                py-5

                sm:px-6
                sm:py-6

                lg:px-7
                lg:py-7
              "
            >
              {/* =========================================== */}
              {/* CALL */}
              {/* =========================================== */}

              <section>
                <SectionHeading>Call Us</SectionHeading>

                <div
                  className="
                    grid
                    grid-cols-1
                    gap-3

                    xl:grid-cols-2
                  "
                >
                  {callItems.map((item) => (
                    <SupportCard key={item.id} {...item} icon={Phone} />
                  ))}
                </div>
              </section>

              {/* =========================================== */}
              {/* EMAIL */}
              {/* =========================================== */}

              <section className="mt-6 sm:mt-7">
                <SectionHeading>Email Us</SectionHeading>

                {emailItems.map((item) => (
                  <SupportCard
                    key={item.id}
                    {...item}
                    icon={Mail}
                    type="email"
                  />
                ))}
              </section>

              {/* =========================================== */}
              {/* HOURS */}
              {/* =========================================== */}

              <section className="mt-5 sm:mt-6">
                <SectionHeading>Support Hours</SectionHeading>

                <div
                  className="
      flex
      flex-col
      gap-3

      rounded-[14px]
      border
      border-[#e2e6ec]
      bg-[#f8f7fc]

      px-4
      py-3.5

      min-[460px]:flex-row
      min-[460px]:items-center
      min-[460px]:justify-between

      sm:px-4
      sm:py-3

      xl:px-5
      xl:py-4
    "
                >
                  <div className="flex min-w-0 items-center gap-3 sm:gap-2.5">
                    <div
                      className="
          flex
          h-10
          w-10
          shrink-0
          items-center
          justify-center

          rounded-full
          bg-white
          text-[#657085]

          sm:h-9
          sm:w-9

          xl:h-11
          xl:w-11
        "
                    >
                      <Clock3
                        size={18}
                        strokeWidth={2}
                        className="xl:h-5 xl:w-5"
                      />
                    </div>

                    <div className="min-w-0">
                      <p
                        className="
            text-[12px]
            font-semibold
            leading-tight
            text-[#172033]

            sm:text-[12px]

            xl:text-[13px]
          "
                      >
                        Always available
                      </p>

                      <p
                        className="
            mt-0.5
            max-w-[170px]
            text-[10px]
            leading-[1.35]
            text-[#8d98aa]

            sm:max-w-[155px]
            sm:text-[10px]

            xl:max-w-none
            xl:text-[11px]
          "
                      >
                        Our support channels are available every day.
                      </p>
                    </div>
                  </div>

                  <div
                    className="
        inline-flex
        w-fit
        shrink-0
        items-center
        justify-center

        rounded-full
        bg-[#281b67]

        px-3.5
        py-1.5

        text-[10px]
        font-semibold
        whitespace-nowrap
        text-white

        sm:px-3
        sm:py-1.5
        sm:text-[10px]

        xl:px-4
        xl:py-2
        xl:text-[12px]
      "
                  >
                    24 × 7 Support
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
